import { usePageCrumbs } from '../lib/crumbs.ts';
import { Folder, FolderGit2, FolderPlus, GitBranch, SquareTerminal, Trash2, TriangleAlert } from '@orgus/ui/icons';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { isStale, machineLiveness } from '@orgus/protocol';
import type { Machine, Project } from '@orgus/protocol';
import { DataGate, PageBody } from '../components/shell/PageBody.tsx';
import { PageActions } from '../components/shell/TopBar.tsx';
import {
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  IconButton,
  ProjectMark,
  ROW_HEIGHT,
  RelativeTime,
  RowStatus,
  Segmented,
  Select,
  Skeleton,
  StatusDot,
  Tooltip,
  VirtualTable,
  baseName,
  cn,
  formatInt,
  machineLabel,
  machineTone,
  plural,
  sessionRowStatus,
  shortPath,
  shortSha,
  toast,
  useDensity,
  useNow,
} from '@orgus/ui';
import type { Column, FilterChip } from '@orgus/ui';
import { openRunCommand } from '../features/jobs/store.ts';
import { AddProjectDialog } from '../features/projects/AddProjectDialog.tsx';
import { Suggestions } from '../features/projects/Suggestions.tsx';
import { useVisibleSuggestions } from '../features/projects/hidden.ts';
import { mockProjectRemoved } from '../features/projects/mockEffects.ts';
import { buildRows, checkoutCount, removeBlocker } from '../features/projects/model.ts';
import { Drift, ProjectCard, ProjectMenu, projectChip } from '../features/projects/ProjectCard.tsx';
import { ProjectsGlance } from '../features/projects/ProjectsGlance.tsx';
import { projectWork } from '../features/projects/work.ts';
import { CARD_SURFACE } from '../features/machines/MachineCard.tsx';
import type { CheckoutRow } from '../features/projects/model.ts';
import { runAction } from '../lib/api.ts';
import { useRegisterCommands } from '../lib/commands.ts';
import type { Command } from '../lib/commands.ts';
import { useOrgus, useMachines } from '../lib/store.ts';
import { useShallow } from 'zustand/react/shallow';
import { OutOfScopeNotice, useOutOfScope } from '../features/teams/OutOfScope.tsx';
import { checkoutPredicate } from '../features/ask/evaluate.ts';
import { MachineRef, SessionRef } from '../features/entities/EntityRef.tsx';
import { removeWorktreesAction } from '../features/act/catalogue.ts';
import { performAct } from '../features/act/run.ts';
import { ViewBar } from '../features/views/ViewBar.tsx';
import { useAskData, useWhere } from '../features/views/useWhere.ts';
import { useProjectProtocol } from '../features/project-health/api.ts';
import { reportsForProject } from '../features/project-health/model.ts';
import type { ProjectReport } from '../features/project-health/model.ts';
import { useHealthParam } from '../features/project-health/ProjectHealthDrawer.tsx';

function ProjectsSkeleton() {
  return (
    <div className="mx-auto flex w-full max-w-(--layout-width-dashboard) flex-col gap-6" role="status" aria-label="Loading projects">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className={cn(CARD_SURFACE, 'flex flex-col gap-3 p-4')}>
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-8 w-16" />
            <Skeleton className="h-1.5" />
          </div>
        ))}
      </div>
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className={cn(CARD_SURFACE, 'flex flex-col gap-4 p-4')}>
          <div className="flex items-center gap-3">
            <Skeleton className="size-9" />
            <Skeleton className="h-4 w-40" />
          </div>
          <Skeleton className="h-1.5" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}

/** The project's group header in the Checkouts lens, at row height: name, counts, one chip, the menu. */
function ProjectGroup({
  project,
  rows,
  highlighted,
  onStopWatching,
  reports,
  onHealth,
}: {
  project: Project | undefined;
  rows: CheckoutRow[];
  highlighted: boolean;
  onStopWatching: (project: Project, machine: Machine) => void;
  /** What the project reports through the project protocol, one per machine. */
  reports: ProjectReport[];
  onHealth: (projectId: string) => void;
}) {
  const machineList = [...new Map(rows.filter((r) => r.machine).map((r) => [r.machine!.id, r.machine!])).values()];
  const chip = projectChip(project, reports);
  const live = rows.filter((r) => r.session).length;
  return (
    <div className={cn('flex h-row min-w-0 items-center gap-3 transition-colors duration-(--duration-slow)', highlighted && 'text-accent-text')}>
      <ProjectMark projectId={project?.id ?? null} name={project?.name ?? '?'} size="xs" />
      <span className="flex min-w-0 flex-1 items-baseline gap-3">
        <span className="truncate text-ui font-medium text-fg">{project?.name ?? 'Unknown project'}</span>
        <span data-meta="" className="hidden shrink-0 text-caption text-fg-subtle num sm:inline">
          {plural(rows.length, 'checkout')}
          {machineList.length > 1 ? ` on ${plural(machineList.length, 'machine')}` : ''}
          {live ? `, ${formatInt(live)} live` : ''}
        </span>
      </span>
      {chip && (
        <Badge data-chip="" tone={chip.tone} variant={chip.variant} className="hidden shrink-0 sm:inline-flex">
          {chip.text}
        </Badge>
      )}
      {project && machineList.length > 0 && <ProjectMenu project={project} machines={machineList} reports={reports} onHealth={onHealth} onStopWatching={onStopWatching} />}
    </div>
  );
}

/** Projects across machines: every checkout and worktree, what lives in it, and how far it has drifted. */
export default function Projects() {
  const now = useNow(5000);
  const density = useDensity();
  const [params, setParams] = useSearchParams();
  const focusProject = params.get('project');
  const focusCheckout = params.get('checkout');
  const { checkouts, sessions, projects, machines, pipelineMap } = useOrgus(
    useShallow((s) => ({ checkouts: s.checkouts, sessions: s.sessions, projects: s.projects, machines: s.machines, pipelineMap: s.pipelines })),
  );
  const lens: 'projects' | 'checkouts' = params.get('lens') === 'checkouts' ? 'checkouts' : 'projects';
  const setLens = (l: 'projects' | 'checkouts') =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (l === 'projects') n.delete('lens');
        else n.set('lens', l);
        return n;
      },
      { replace: true },
    );
  const machineList = useMachines();
  const focusName = focusProject ? projects[focusProject]?.name : undefined;
  usePageCrumbs(focusName ? [{ label: focusName }] : []);
  const pp = useProjectProtocol().state;
  const [, openHealth] = useHealthParam();
  // A link to a project or checkout the chosen workspace hides: say so, with the way to see it.
  const hidden = useOutOfScope(
    focusCheckout && !checkouts[focusCheckout] ? { checkout: focusCheckout } : focusProject && !projects[focusProject] ? { project: focusProject } : null,
  );
  const suggestions = useVisibleSuggestions();
  const machineFilter = params.get('machine') ?? 'all';
  const setMachineFilter = (id: string) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        if (id === 'all') n.delete('machine');
        else n.set('machine', id);
        n.delete('view');
        return n;
      },
      { replace: true },
    );
  const where = useWhere('checkouts');
  const askData = useAskData(now);
  const wherePred = useMemo(() => checkoutPredicate(where.clauses, askData), [where.clauses, askData]);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<CheckoutRow | null>(null);
  const [unwatching, setUnwatching] = useState<{ project: Project; machine: Machine } | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  // A copy the browser refused, said over the table: errors never go in a toast.
  const [copyError, setCopyError] = useState<unknown>(null);

  const rows = useMemo(() => {
    const all = Object.values(checkouts);
    return buildRows(all, Object.values(sessions), projects, machines)
      .filter((r) => (machineFilter === 'all' || r.checkout.machineId === machineFilter) && wherePred(r.checkout))
      .sort(
        (a, b) =>
          (a.project?.name ?? '').localeCompare(b.project?.name ?? '') ||
          Number(a.checkout.kind === 'worktree') - Number(b.checkout.kind === 'worktree') ||
          a.checkout.path.localeCompare(b.checkout.path),
      );
  }, [checkouts, sessions, projects, machines, machineFilter, wherePred]);

  const totalCheckouts = Object.keys(checkouts).length;
  const pipelines = useMemo(
    () => Object.values(pipelineMap).filter((p) => machineFilter === 'all' || p.machineId === machineFilter),
    [pipelineMap, machineFilter],
  );
  const work = useMemo(() => projectWork(rows, pipelines, projects, now), [rows, pipelines, projects, now]);

  /** A checkout's actions: run a command in it, and remove it when it is a worktree (or say why not). */
  const checkoutActions = (r: CheckoutRow) => {
    const name = baseName(r.checkout.path);
    const run = machines[r.checkout.machineId]?.online && !r.checkout.error && (
      <IconButton label={`Run a command in ${name}`} icon={<SquareTerminal />} size="xs" onClick={() => openRunCommand({ checkoutId: r.checkout.id })} />
    );
    if (r.checkout.kind !== 'worktree') return run || null;
    const why = removeBlocker(r, now);
    return (
      <>
        {run}
        {why ? (
          <Tooltip content={why}>
            <span tabIndex={0} aria-label={`Cannot remove ${name}: ${why}`} className="inline-flex rounded-control">
              <IconButton label={`Remove worktree ${name}`} noTooltip icon={<Trash2 />} variant="danger" size="xs" disabled />
            </span>
          </Tooltip>
        ) : (
          <IconButton label={`Remove worktree ${name}`} icon={<Trash2 />} variant="danger" size="xs" onClick={() => setRemoving(r)} />
        )}
      </>
    );
  };
  // One machine in view: the machine column says the same thing on every row, so it goes.
  const oneMachine = new Set(rows.map((r) => r.checkout.machineId)).size <= 1;

  // A deep link (?project=) scrolls its group into view and marks it briefly.
  useEffect(() => {
    if (!focusProject || !rows.length) return;
    const el = document.querySelector(`[data-group="${CSS.escape(focusProject)}"]`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setFlash(focusProject);
    const t = setTimeout(() => {
      setFlash(null);
      setParams(
        (p) => {
          const n = new URLSearchParams(p);
          n.delete('project');
          return n;
        },
        { replace: true },
      );
    }, 1600);
    return () => clearTimeout(t);
  }, [focusProject, rows.length, setParams]);

  const columns = useMemo<Column<CheckoutRow>[]>(() => {
    const cols: Column<CheckoutRow>[] = [
      {
        key: 'checkout',
        header: 'Checkout',
        grow: true,
        text: (r) => `${r.project?.name ?? ''} ${r.checkout.path} ${r.checkout.branch ?? ''} ${r.checkout.headSubject}`,
        sortValue: (r) => baseName(r.checkout.path),
        cell: (r) => {
          const c = r.checkout;
          const worktree = c.kind === 'worktree';
          const Icon = worktree ? GitBranch : Folder;
          const name = baseName(c.path);
          const branch = c.branch ?? `detached at ${shortSha(c.head)}`;
          return (
            <span className="flex min-w-0 items-center gap-2">
              <Tooltip content={worktree ? 'Worktree' : 'Main checkout'}>
                <Icon className="size-4 shrink-0 text-fg-subtle" aria-label={worktree ? 'Worktree' : 'Main checkout'} />
              </Tooltip>
              <Tooltip content={c.error ? `Could not read git: ${c.error}` : `${shortPath(c.path, 80)}\n${shortSha(c.head)} ${c.headSubject}`} delay={500}>
                <span tabIndex={-1} className="truncate font-medium text-fg">
                  {name}
                </span>
              </Tooltip>
              {c.error ? (
                <span data-meta="" className="flex min-w-0 items-center gap-1 truncate text-caption text-status-danger">
                  <TriangleAlert className="size-3 shrink-0" aria-hidden />
                  Could not read git
                </span>
              ) : branch !== name ? (
                <span data-meta="" className="hidden min-w-0 truncate font-mono text-caption text-fg-subtle sm:inline">
                  {branch}
                </span>
              ) : null}
            </span>
          );
        },
      },
      {
        key: 'session',
        header: 'Session',
        // Its cell is a fixed 240 px: on a tablet it pushed the row actions out of the table.
        hideBelow: 'lg',
        text: (r) => r.session?.title ?? '',
        sortValue: (r) => (r.session ? r.session.status : null),
        cell: (r) =>
          r.session ? (
            <span className="flex w-60 min-w-0 items-center gap-2 xl:w-72">
              <RowStatus {...sessionRowStatus(r.session.status)} hideLabel />
              <SessionRef id={r.session.id} className="min-w-0 truncate text-fg-muted" />
            </span>
          ) : null,
      },
    ];
    if (!oneMachine) {
      cols.push({
        key: 'machine',
        header: 'Machine',
        hideBelow: 'sm',
        text: (r) => r.machine?.name ?? '',
        sortValue: (r) => r.machine?.name ?? '',
        cell: (r) =>
          r.machine ? (
            <span className="flex items-center gap-2 text-fg-muted">
              <StatusDot tone={machineTone(r.machine, now)} size="xs" label={machineLabel(r.machine, now)} />
              <MachineRef id={r.machine.id} />
            </span>
          ) : (
            <span className="text-fg-subtle">Removed machine</span>
          ),
      });
    }
    cols.push(
      {
        key: 'drift',
        header: 'Ahead, behind',
        hideBelow: 'sm',
        type: 'number',
        sortValue: (r) => r.checkout.behind,
        cell: (r) => <Drift row={r} />,
      },
      {
        key: 'dirty',
        header: 'Changes',
        type: 'number',
        hideBelow: 'sm',
        sortValue: (r) => r.checkout.dirty,
        cell: (r) => (r.checkout.dirty ? <span className="text-fg">{formatInt(r.checkout.dirty)}</span> : null),
      },
      {
        key: 'updated',
        header: 'Updated',
        type: 'date',
        hideBelow: 'lg',
        sortValue: (r) => r.checkout.updatedAt,
        cell: (r) => {
          const stale = isStale(r.checkout.updatedAt, 'checkout', now);
          return (
            <Tooltip content={stale ? 'Not refreshed recently: its machine may be busy or offline' : 'Last read from git'}>
              <span tabIndex={-1} className="text-caption text-fg-subtle num">
                <RelativeTime ts={r.checkout.updatedAt} />
              </span>
            </Tooltip>
          );
        },
      },
    );
    return cols;
  }, [now, oneMachine]);

  const cmds = useMemo<Command[]>(
    () => [
      { id: 'project.add', title: 'Add project', subtitle: 'Watch a git repository on a machine', group: 'Actions', icon: FolderPlus, order: 21, perform: () => setAdding(true) },
    ],
    [],
  );
  useRegisterCommands(cmds, [cmds]);

  const machineChips: FilterChip[] =
    machineFilter !== 'all' ? [{ id: 'machine', field: 'Machine', value: machines[machineFilter]?.name ?? 'Removed machine', onRemove: () => setMachineFilter('all') }] : [];

  const removingCopy = removing
    ? {
        name: baseName(removing.checkout.path),
        branch: removing.checkout.branch,
        ahead: removing.checkout.ahead,
        base: removing.project?.defaultBranch ?? 'main',
      }
    : null;

  return (
    <PageBody wide>
      <PageActions>
        <Tooltip content="Watch a git repository on a machine">
          <Button variant="secondary" size="sm" icon={<FolderPlus className="text-accent-text" />} onClick={() => setAdding(true)}>
            Add project
          </Button>
        </Tooltip>
      </PageActions>
      <DataGate skeleton={<ProjectsSkeleton />}>
        <div className="mx-auto flex w-full max-w-(--layout-width-dashboard) flex-col gap-6">
          {totalCheckouts > 0 && <ProjectsGlance work={work} pipelines={pipelines} />}
          {hidden && <OutOfScopeNotice what={focusCheckout && !checkouts[focusCheckout] ? 'checkout' : 'project'} info={hidden} />}
          {suggestions.length > 0 && <Suggestions suggestions={suggestions} defaultOpen={totalCheckouts === 0} />}
          {copyError != null && (
            <ErrorState
              variant="inline"
              error={copyError}
              action={
                <Button size="sm" variant="ghost" onClick={() => setCopyError(null)}>
                  Dismiss
                </Button>
              }
            />
          )}
          {totalCheckouts > 0 && (
            <div className="flex min-w-0 flex-wrap items-center gap-3">
              <Segmented
                label="Show"
                value={lens}
                onChange={setLens}
                options={[
                  { value: 'projects', label: 'Projects' },
                  { value: 'checkouts', label: 'Checkouts' },
                ]}
              />
              {lens === 'projects' && machineList.length > 1 && (
                <Select
                  size="sm"
                  aria-label="Machine"
                  value={machineFilter}
                  onValueChange={setMachineFilter}
                  options={[{ value: 'all', label: 'All machines' }, ...machineList.map((m) => ({ value: m.id, label: m.name }))]}
                  wrapperClassName="w-44"
                />
              )}
              <span className="ml-auto text-caption text-fg-subtle num">
                {plural(work.length, 'project')}, {plural(rows.length, 'checkout')}
              </span>
            </div>
          )}
          {totalCheckouts === 0 ? (
            <div className={CARD_SURFACE}>
              <EmptyState
                size="page"
                icon={<FolderGit2 />}
                title="No projects yet"
                description={machineList.length ? 'Watch a git repository on any machine to see its worktrees and sessions' : 'Connect a machine first, then add a repository on it'}
                action={
                  <Button variant="primary" size="lg" icon={<FolderPlus />} onClick={() => setAdding(true)}>
                    Add project
                  </Button>
                }
              />
            </div>
          ) : lens === 'projects' ? (
            work.length === 0 ? (
              <div className={CARD_SURFACE}>
                <EmptyState
                  title="No checkouts on this machine"
                  description="Pick another machine, or add a repository on this one"
                  action={
                    <Button size="sm" icon={<FolderPlus />} onClick={() => setAdding(true)}>
                      Add project
                    </Button>
                  }
                />
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {work.map((w) => {
                  const reports = reportsForProject(pp, w.id);
                  return (
                    <ProjectCard
                      key={w.id}
                      work={w}
                      now={now}
                      highlighted={flash === w.id}
                      chip={projectChip(w.project, reports)}
                      checkoutActions={checkoutActions}
                      menu={
                        w.project && w.machines.length > 0 ? (
                          <ProjectMenu project={w.project} machines={w.machines} reports={reports} onHealth={openHealth} onStopWatching={(project, machine) => setUnwatching({ project, machine })} />
                        ) : undefined
                      }
                    />
                  );
                })}
              </div>
            )
          ) : (
            <VirtualTable
              label="Checkouts and worktrees"
              rows={rows}
              columns={columns}
              rowKey={(r) => r.checkout.id}
              searchable={{ placeholder: 'Search checkouts', shortcut: true }}
              groupHeight={ROW_HEIGHT[density]}
              revealGroup={focusProject}
              selection={{
                noun: 'checkout',
                actions: (sel) => {
                  const removable = sel.filter((r) => r.checkout.kind === 'worktree' && !removeBlocker(r, now));
                  const blocked = sel.length - removable.length;
                  return [
                    {
                      id: 'remove',
                      label: removable.length ? `Remove ${plural(removable.length, 'worktree')}` : 'Remove worktrees',
                      icon: <Trash2 />,
                      danger: true,
                      disabled: !removable.length,
                      hint: blocked
                        ? `${plural(blocked, 'checkout')} cannot go: main checkouts, uncommitted changes or a live session stay.`
                        : 'Asks you to confirm by name. Policy still applies.',
                      onClick: () => void performAct(removeWorktreesAction(removable.map((r) => r.checkout), { machines, projects }, `bulk.worktree.remove:${removable.length}`)),
                    },
                    {
                      id: 'paths',
                      label: 'Copy paths',
                      onClick: () => {
                        setCopyError(null);
                        void navigator.clipboard
                          ?.writeText(sel.map((r) => r.checkout.path).join('\n'))
                          .then(() => toast.success(`Copied ${plural(sel.length, 'path')}`))
                          .catch(() =>
                            setCopyError({ what: 'The paths were not copied.', why: 'The browser did not allow it.', next: 'Try again, or select the paths and copy them by hand.' }),
                          );
                      },
                    },
                  ];
                },
              }}
              viewKey={`${machineFilter}|${where.text}`}
              filters={
                <>
                  {machineList.length > 1 && (
                    <Select
                      size="sm"
                      aria-label="Machine"
                      value={machineFilter}
                      onValueChange={setMachineFilter}
                      options={[{ value: 'all', label: 'All machines' }, ...machineList.map((m) => ({ value: m.id, label: m.name }))]}
                      wrapperClassName="w-44"
                    />
                  )}
                  <ViewBar entity="checkouts" where={where} extraChips={machineChips} onClearExtra={() => setMachineFilter('all')} density={false} />
                </>
              }
              toolbarEnd={(shown) => <span className="num">{checkoutCount(shown, rows.length)}</span>}
              groupBy={(r) => r.checkout.projectId}
              renderGroup={(id, groupRows) => (
                <ProjectGroup
                  project={projects[id]}
                  rows={groupRows}
                  highlighted={flash === id}
                  onStopWatching={(project, machine) => setUnwatching({ project, machine })}
                  reports={reportsForProject(pp, id)}
                  onHealth={openHealth}
                />
              )}
              empty={
                <EmptyState
                  title="No checkouts on this machine"
                  description="Pick another machine, or add a repository on this one"
                  action={
                    <Button size="sm" icon={<FolderPlus />} onClick={() => setAdding(true)}>
                      Add project
                    </Button>
                  }
                />
              }
              rowActions={checkoutActions}
            />
          )}
        </div>
      </DataGate>

      <AddProjectDialog open={adding} onOpenChange={setAdding} />

      <ConfirmDialog
        open={!!removing}
        onOpenChange={(v) => !v && setRemoving(null)}
        title={`Remove worktree ${removingCopy?.name ?? ''}?`}
        description={
          removingCopy
            ? `This deletes the folder on ${removing?.machine?.name ?? 'its machine'} and its git worktree entry. ${
                removingCopy.branch ? `The branch ${removingCopy.branch} stays${removingCopy.ahead ? `, with its ${plural(removingCopy.ahead, 'commit')} not on ${removingCopy.base}` : ''}.` : ''
              } Linked folders inside it are unlinked; their targets are not touched.`
            : ''
        }
        confirmLabel="Remove worktree"
        onConfirm={async () => {
          if (!removing) return;
          await runAction(removing.checkout.machineId, 'worktree.remove', { checkoutId: removing.checkout.id });
          toast.success('Worktree removed', `${shortPath(removing.checkout.path)} on ${removing.machine?.name ?? 'its machine'}.`);
        }}
      >
        {removing && <p className="truncate rounded-control border border-border bg-bg px-3 py-2 font-mono text-caption text-fg-muted">{removing.checkout.path}</p>}
      </ConfirmDialog>

      <ConfirmDialog
        open={!!unwatching}
        onOpenChange={(v) => !v && setUnwatching(null)}
        title={`Stop watching ${unwatching?.project.name ?? ''} on ${unwatching?.machine.name ?? ''}?`}
        description={`Its checkouts on ${unwatching?.machine.name ?? 'that machine'} leave Orgus. Nothing on disk changes, and you can add it again any time.`}
        confirmLabel="Stop watching"
        onConfirm={async () => {
          if (!unwatching) return;
          const { project, machine } = unwatching;
          if (machineLiveness(machine, Date.now()) !== 'online') {
            throw { what: `${machine.name} is not reporting.`, why: 'Only a connected machine can change what it watches.', next: 'Bring it online, then try again.' };
          }
          await runAction(machine.id, 'project.remove', { projectId: project.id, machineId: machine.id });
          mockProjectRemoved(project.id, machine.id);
          toast.success(`Stopped watching ${project.name}`, `on ${machine.name}.`);
        }}
      />
    </PageBody>
  );
}
